import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import axios from "../../utils/api.js";
import DarImage from "../../assets/dar.png";

const sortByQuestionNumber = (list) =>
  [...(list || [])].sort((a, b) => {
    const numA = Number(a.questionNumber);
    const numB = Number(b.questionNumber);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
    return String(a.questionNumber).localeCompare(String(b.questionNumber));
  });

const QuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [distributorName, setDistributorName] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [distributorSelected, setDistributorSelected] = useState(false);
  const navigate = useNavigate();

  const sortedQuestions = useMemo(() => sortByQuestionNumber(questions), [questions]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get("/questions", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setQuestions(response.data);
      } catch (err) {
        setError("Failed to load questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    const fetchDistributors = async () => {
      try {
        const response = await axios.get("/distributors", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setDistributors(response.data);
      } catch (err) {
        console.error("Error fetching distributors:", err);
      }
    };

    fetchQuestions();
    fetchDistributors();
  }, []);

  const handleDistributorChange = (e) => {
    setDistributorName(e.target.value);
    setDistributorSelected(true);
  };

  const handleAnswerChange = (questionId, questionNumber, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionNumber]: { questionId, questionNumber, answer },
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < sortedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const allAnswered =
    sortedQuestions.length > 0 &&
    Object.keys(answers).length === sortedQuestions.length;

  const handleSubmit = async () => {
    if (!distributorName.trim()) {
      alert("⚠️ Please enter the distributor name before submitting.");
      return;
    }
    if (!allAnswered) {
      alert("⚠️ Please answer all questions before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        "/submit-answers",
        { distributorName, answers: Object.values(answers) },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setSuccessMessage("✅ Answers submitted successfully!");
      setAnswers({});
      setDistributorName("");
      setTimeout(() => {
        setSuccessMessage("");
        setCurrentQuestionIndex(0);
      }, 2000);
    } catch (err) {
      alert("❌ Failed to submit answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  if (loading) {
    return (
      <div
        style={{
          ...pageStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#667eea" }} />
        <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            textAlign: "center",
            color: "#dc2626",
            fontSize: "1rem",
            marginTop: 80,
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      {distributorSelected && (
        <div
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#1e293b",
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: "8px 14px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            zIndex: 10,
          }}
        >
          Distributor: {distributorName}
        </div>
      )}

      {!distributorSelected ? (
        <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
          <div style={{ marginTop: 20, marginBottom: 28 }}>
            <h1
              style={{
                margin: 0,
                marginBottom: 8,
                fontSize: "1.85rem",
                fontWeight: 700,
                letterSpacing: "-0.5px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Select a Distributor
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: "0.95rem",
                color: "#64748b",
                fontWeight: 400,
              }}
            >
              Choose a distributor to start answering questions
            </p>
          </div>
          <div
            style={{
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >
            <label
              style={{
                display: "block",
                textAlign: "left",
                marginBottom: 8,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#64748b",
              }}
            >
              Distributor
            </label>
            <select
              value={distributorName}
              onChange={handleDistributorChange}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "0.65rem 2.25rem 0.65rem 0.85rem",
                fontSize: "1rem",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                outline: "none",
                backgroundColor: "#fff",
                appearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%2364748b' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1.25rem",
                cursor: "pointer",
              }}
            >
              <option value="" disabled>
                Select Distributor
              </option>
              {distributors.map((d) => (
                <option key={d._id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
            <img
              src={DarImage}
              alt="DAR"
              style={{
                display: "block",
                margin: "24px auto 0",
                width: 140,
                height: "auto",
              }}
            />
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginTop: 20, marginBottom: 24 }}>
            <h1
              style={{
                margin: 0,
                marginBottom: 8,
                fontSize: "1.85rem",
                fontWeight: 700,
                letterSpacing: "-0.5px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Answer the Questions
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: "0.95rem",
                color: "#64748b",
                fontWeight: 400,
              }}
            >
              Progress: {Object.keys(answers).length} / {sortedQuestions.length}
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 24,
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            }}
          >
            <h4
              style={{
                margin: "0 0 20px",
                fontSize: "1.15rem",
                fontWeight: 600,
                color: "#1e293b",
                lineHeight: 1.4,
              }}
            >
              {sortedQuestions[currentQuestionIndex].questionNumber}.{" "}
              {sortedQuestions[currentQuestionIndex].question}
            </h4>
            <div style={{ marginTop: 12 }}>
              {sortedQuestions[currentQuestionIndex].answerType ===
              "multiple-choice" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {sortedQuestions[currentQuestionIndex].options.map(
                    (option, index) => (
                      <label
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          borderRadius: 8,
                          backgroundColor: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="radio"
                          name={`question-${sortedQuestions[currentQuestionIndex].questionNumber}`}
                          value={option.option}
                          onChange={() =>
                            handleAnswerChange(
                              sortedQuestions[currentQuestionIndex]._id,
                              sortedQuestions[currentQuestionIndex].questionNumber,
                              option.option
                            )
                          }
                          checked={
                            answers[
                              sortedQuestions[currentQuestionIndex].questionNumber
                            ]?.answer === option.option
                          }
                          style={{ cursor: "pointer" }}
                        />
                        <span style={{ fontSize: "1rem" }}>{option.option}</span>
                      </label>
                    )
                  )}
                </div>
              ) : (
                <textarea
                  rows={4}
                  placeholder="Your answer"
                  value={
                    answers[
                      sortedQuestions[currentQuestionIndex].questionNumber
                    ]?.answer || ""
                  }
                  onChange={(e) =>
                    handleAnswerChange(
                      sortedQuestions[currentQuestionIndex]._id,
                      sortedQuestions[currentQuestionIndex].questionNumber,
                      e.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "0.75rem 1rem",
                    fontSize: "1rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    outline: "none",
                    resize: "vertical",
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 24,
                gap: 12,
              }}
            >
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: 8,
                  border: "1px solid #e2e8f0",
                  backgroundColor: currentQuestionIndex === 0 ? "#f1f5f9" : "#fff",
                  color: currentQuestionIndex === 0 ? "#94a3b8" : "#64748b",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: currentQuestionIndex === 0 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>
              {currentQuestionIndex < sortedQuestions.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  style={{
                    padding: "0.6rem 1.25rem",
                    borderRadius: 8,
                    border: "none",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Next
                </button>
              ) : null}
            </div>
            {allAnswered && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: "100%",
                  marginTop: 20,
                  padding: "0.75rem 1rem",
                  borderRadius: 8,
                  border: "none",
                  background: submitting
                    ? "#94a3b8"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: submitting ? "not-allowed" : "pointer",
                  boxShadow: submitting ? "none" : "0 2px 8px rgba(102, 126, 234, 0.3)",
                }}
              >
                {submitting ? "Saving..." : "Save answers"}
              </button>
            )}
            {successMessage && (
              <div
                style={{
                  marginTop: 16,
                  color: "#16a34a",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                }}
              >
                {successMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionsPage;
